import React from 'react';
import { Helmet } from 'react-helmet';
import { FaLinkedin } from 'react-icons/fa';
import { motion } from 'framer-motion';
import '../../styles/theme.css';
import jayeshImage from '../../assets/images/team/jayesh-navalbhai-gadhvi.jpg';
import { fadeInUp, staggerContainer, staggerItem, getAnimationProps, getInitialAnimationProps, getInitialStaggerProps } from '../../utils/animations';

const aboutHighlights = [
  {
    title: 'Who We Are',
    text: `OptiBill Analytics is a professional Medical Billing & End-to-End RCM service provider based in India and serving healthcare providers across the globe. Our mission is to simplify revenue management for medical practices by offering accurate billing, clean claim submissions, and reliable payment cycle support.`
  },
  {
    title: 'Our Vision',
    text: `To become the most trusted global partner for healthcare revenue cycle management with a focus on accuracy, transparency, and growth for our clients.`
  },
  {
    title: 'Our Mission',
    text: `To deliver world-class RCM solutions that increase profitability, reduce administrative workload, and enhance financial performance for medical practices.`
  },
  {
    title: 'Global Delivery Model',
    text: `OptiBill Analytics operates on a secure and efficient global delivery model designed to provide high-quality medical billing and revenue cycle management services at optimized costs.\n\nOur core operations and delivery teams are based in India, supported by well-defined processes, trained billing professionals, and robust quality control systems. This allows us to deliver consistent, accurate, and timely services to healthcare providers across the United States of America and other global markets.\n\nClient communication, reporting, and account management are handled through dedicated account managers, ensuring seamless coordination, transparency, and real-time updates.\n\nAll workflows strictly follow HIPAA-compliant data security protocols, with secure access controls, encrypted systems, and continuous monitoring to protect patient information at every stage.\n\nThis global delivery approach enables us to offer cost-effective solutions, faster turnaround times, and scalable support—without compromising on quality, compliance, or reliability.`
  }
];

const deliveryGlance = [
  { label: 'Delivery Location', value: 'India-based RCM operations' },
  { label: 'Client Coverage', value: 'United States of America & global healthcare providers' },
  { label: 'Data Security', value: 'HIPAA-compliant workflows & secure systems' },
  { label: 'Client Support', value: 'Dedicated account manager & real-time reporting' },
];

// Leadership Team Members
const leadershipTeam = [
  {
    name: "Jayesh Navalbhai Gadhvi",
    title: "Founder & CEO",
    bio: "With over 5 years of experience in Healthcare Revenue Cycle Management, Jayesh founded OptiBill Analytics with a vision to deliver world-class RCM services that help medical practices thrive financially.",
    expertise: ["Revenue Cycle Strategy", "Healthcare Operations", "Business Development"],
    image: jayeshImage,
    linkedin: "https://www.linkedin.com/in/jayesh-gadhvi-868196199/"
  },
];

function About() {
  return (
    <>
      <Helmet>
        <title>About Us | OptiBill Analytics - Expert Medical Billing Team</title>
        <meta name="description" content="Learn about OptiBill Analytics - India-based medical billing and RCM service provider serving healthcare providers globally with HIPAA-compliant solutions and dedicated account management." />
        <meta name="keywords" content="about OptiBill Analytics, medical billing company, RCM service provider, healthcare billing team, HIPAA compliant billing, global delivery model" />
        <meta property="og:title" content="About OptiBill Analytics - Expert Medical Billing Team" />
        <meta property="og:description" content="Professional medical billing team serving healthcare providers globally with HIPAA-compliant RCM solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.optibillanalytics.com/about" />
        <link rel="canonical" href="https://www.optibillanalytics.com/about" />
      </Helmet>
    <section className="page-shell">
      <motion.div className="page-header" {...getInitialAnimationProps(fadeInUp, 0.2)}>
        <h1 className="page-title">About OptiBill Analytics</h1>
        <span className="about-sub-underline" />
      </motion.div>
      <motion.div 
        className="page-container"
        style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr', gap: 28 }}
        {...getInitialStaggerProps(0.4)}
      >
        {aboutHighlights.map((item, idx) => (
          <motion.div 
            key={idx} 
            className="section-panel"
            variants={staggerItem}
          >
            <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 12, textAlign: 'center' }}>{item.title}</h2>
            <span className="about-sub-underline" style={{ marginBottom: 18, marginTop: -8, display: 'block' }} />
            {item.text.split('\n').map((p, i) => (
              <p key={i} style={{ color: '#5A6478', fontSize: 17, marginBottom: 12, lineHeight: 1.7 }}>{p}</p>
            ))}
          </motion.div>
        ))}
        <motion.div 
          className="section-panel"
          variants={staggerItem}
        >
          <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 14, textAlign: 'center' }}>Global Delivery Model – At a Glance</h3>
          <span className="about-sub-underline" style={{ marginBottom: 18, marginTop: -8, display: 'block' }} />
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', rowGap: 10 }}>
            {deliveryGlance.map((item, idx) => (
              <li key={idx} style={{ display: 'grid', gridTemplateColumns: '170px 1fr', alignItems: 'start', columnGap: 0, fontSize: 16, color: '#2B4278', marginBottom: 0 }}>
                <span style={{ fontWeight: 600, textAlign: 'right', paddingRight: 16 }}>{item.label}:</span>
                <span style={{ color: '#5A6478', fontWeight: 500, textAlign: 'left' }}>{item.value}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </motion.div>

      {/* Leadership Team Section */}
      <div className="page-container" style={{ margin: '48px auto 24px', maxWidth: 960 }}>
        <motion.div className="page-header" {...getAnimationProps(fadeInUp)}>
          <h2 className="text-navy fw-bold" style={{ fontSize: 32, marginBottom: 8 }}>Meet Our Leadership Team</h2>
          <span className="about-sub-underline" />
          <p style={{ color: '#5A6478', fontSize: 17, maxWidth: 700, margin: '16px auto 0', lineHeight: 1.6 }}>
            Our experienced team of healthcare billing professionals is dedicated to your success.
          </p>
        </motion.div>
        <div className="container">
          <motion.div 
            className="row g-4 justify-content-center"
            {...getAnimationProps(staggerContainer)}
          >
            {leadershipTeam.map((member, idx) => (
                <motion.div 
                  key={idx} 
                  className="col-md-6 col-lg-4"
                  variants={staggerItem}
                >
                  <div style={{ padding: '32px 24px', textAlign: 'center', height: '100%' }} className="service-card">
                <div style={{ width: '140px', height: '140px', borderRadius: '50%', margin: '0 auto 20px', overflow: 'hidden', boxShadow: '0 8px 20px rgba(43,66,120,0.15)', border: '3px solid #2EB8A8' }}>
                  <img src={member.image} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <h3 className="text-navy fw-bold" style={{ fontSize: 22, marginBottom: 6 }}>{member.name}</h3>
                <p style={{ color: '#2EB8A8', fontSize: 16, fontWeight: 600, marginBottom: 14 }}>{member.title}</p>
                <p style={{ color: '#5A6478', fontSize: 15, lineHeight: 1.6, marginBottom: 16, textAlign: 'left' }}>{member.bio}</p>
                <div style={{ borderTop: '1px solid #e6eef7', paddingTop: 16, marginTop: 16 }}>
                  <p style={{ color: '#2B4278', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Areas of Expertise:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {member.expertise.map((skill, i) => (
                      <span key={i} style={{ background: '#e6f7f5', color: '#2B4278', padding: '4px 12px', borderRadius: 16, fontSize: 13, fontWeight: 500 }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', color: '#2EB8A8', marginTop: 16, fontSize: 28, transition: 'transform 0.2s' }}>
                  <FaLinkedin />
                </a>
              </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>    </>  );
}

export default About;
